'use client';

import { useState, useRef } from 'react';
import { useReveal } from '@/components/marketing/engine/use-reveal';

/* v7 FAQ — shadcn-style accordion. Smooth height, clean transitions, premium spacing. */

interface FAQItemData {
  q: string;
  a: string;
}

const FAQ_DATA: FAQItemData[] = [
  { q: 'How is VHire different from an applicant tracking system?',
    a: 'VHire brings candidate applications, assessments, interviews, and review workflows into one workspace. The tools available to a hiring team depend on its configuration and enabled services.' },
  { q: 'What does "verified" actually mean?',
    a: 'A verified label should refer to a specific completed check and its recorded result. It does not mean every candidate claim has been independently confirmed.' },
  { q: 'Can we see why a decision was made?',
    a: 'Reviewers can use the assessment results, interview records, notes, and flags available in the configured workflow. The hiring team remains responsible for its decision.' },
  { q: 'How long does it take to get started?',
    a: 'Setup time depends on the workflow, integrations, data, and checks a team enables. Those requirements should be reviewed before launch.' },
  { q: 'How is candidate data handled?',
    a: 'VHire processes candidate data for the configured hiring workflow. Candidates should review the applicable notice and consent screens, and the VHire Privacy Policy explains how to raise a privacy request.' },
];

interface FAQItemProps {
  item: FAQItemData;
  open: boolean;
  onToggle: () => void;
}

function FAQItem({ item, open, onToggle }: FAQItemProps) {
  const inner = useRef<HTMLParagraphElement>(null);
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <button onClick={onToggle} aria-expanded={open} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px',
        padding: '22px 4px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        color: open ? 'var(--text)' : 'var(--text)', fontSize: '15.5px', fontWeight: 500, letterSpacing: '-0.01em' }}>
        <span>{item.q}</span>
        <span style={{ flexShrink: 0, width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: open ? 'var(--indigo-light)' : 'var(--text-3)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .35s var(--ease), color .2s' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </span>
      </button>
      <div style={{ height: open ? (inner.current ? inner.current.scrollHeight : 'auto') : 0, overflow: 'hidden',
        transition: 'height .4s var(--ease)' }}>
        <p ref={inner} style={{ padding: '0 4px 24px', fontSize: '13.5px', color: 'var(--text-2)', lineHeight: 1.65, maxWidth: '620px' }}>{item.a}</p>
      </div>
    </div>
  );
}

export function FAQSection() {
  const { ref, shown } = useReveal(0.14);
  const [open, setOpen] = useState<number>(0);
  return (
    <section ref={ref} id="faq" className="section" style={{ background: 'var(--bg-veil)', paddingTop: '120px', paddingBottom: '120px' }}>
      <div className="section-inner" style={{ maxWidth: '760px' }}>
        <div className="faq-head" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', marginBottom: '44px' }}>
          <span className="label" style={{ color: 'var(--text-2)', opacity: shown ? 1 : 0, transition: 'opacity .6s' }}>FAQ</span>
          <h2 className="serif" style={{ fontSize: 'var(--h2)', lineHeight: 1.04, letterSpacing: '-0.01em',
            opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(12px)', transition: 'all .9s var(--ease) .1s' }}>
            Questions, answered plainly.
          </h2>
        </div>
        <div style={{ opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(12px)', transition: 'all .9s var(--ease) .2s', borderTop: '1px solid var(--border)' }}>
          {FAQ_DATA.map((item, i) => (
            <FAQItem key={i} item={item} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default FAQSection;
